import { useCountByStatus } from "@/api/author/author";
import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Author } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * AuthorLinks component
 * @returns {JSX.Element}
 */
const AuthorLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAuthors, setPaginationRelatedAuthors] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAuthor",
    getPaginationRelatedAuthors.pageSize,
    getPaginationRelatedAuthors.page * getPaginationRelatedAuthors.pageSize,
  );

  const { data: countRelatedAuthors } = useCountRelatedFields(id, type, [
    "relatedAuthor",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAuthor",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAuthor",
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const { data: notRelatedAuthors } = useGetNotRelatedResource<Author>({
    resultType: "author",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (photographyId: string) => {
      deleteRelation(photographyId);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Author, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "artistesAuteursDescriptionPrenom",
        header: t("artistesAuteursDescriptionPrenom"),
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.artistesAuteursDescriptionPrenom} />
          );
        },
      },
      {
        accessorKey: "artistesAuteursDescriptionNom",
        header: t("artistesAuteursDescriptionNom"),
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.artistesAuteursDescriptionNom} />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="author"
                  id={row.original.id}
                  elements={[
                    {
                      label: t("artistesAuteursDescriptionPrenom"),
                      value: row.original.artistesAuteursDescriptionPrenom,
                      type: "text",
                    },
                    {
                      label: t("artistesAuteursDescriptionNom"),
                      value: row.original.artistesAuteursDescriptionNom,
                      type: "text",
                    },
                    {
                      label: t("artistesAuteursDescriptionResumeDates"),
                      value: row.original.artistesAuteursDescriptionResumeDates,
                      type: "text",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="author"
                id={row.original.id}
                elements={[
                  {
                    label: t("artistesAuteursDescriptionPrenom"),
                    value: row.original.artistesAuteursDescriptionPrenom,
                    type: "text",
                  },
                  {
                    label: t("artistesAuteursDescriptionNom"),
                    value: row.original.artistesAuteursDescriptionNom,
                    type: "text",
                  },
                  {
                    label: t("artistesAuteursDescriptionResumeDates"),
                    value: row.original.artistesAuteursDescriptionResumeDates,
                    type: "text",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedAuthors")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode ? (notRelatedAuthors?.data ?? []) : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: totalSize,
              }
            : {
                pagination: getPaginationRelatedAuthors,
                setPage: setPaginationRelatedAuthors,
                totalSize: countRelatedAuthors?.relatedAuthor,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default AuthorLinks;
